import React, { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { format, parseISO } from "date-fns";
import {
  Eye, Users, MousePointerClick, Timer, LogOut, UserPlus, CalendarCheck,
  Loader2, Globe, Smartphone, DoorOpen,
} from "lucide-react";

const RANGES = [
  { label: "7 days", value: 7 },
  { label: "30 days", value: 30 },
  { label: "90 days", value: 90 },
  { label: "All time", value: 0 },
];

function fmtDuration(ms) {
  const s = Math.round((ms || 0) / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

function StatCard({ icon: Icon, label, value, testId, accent = "text-primary" }) {
  return (
    <div data-testid={testId} className="border border-white/10 bg-[#0A0A0B] p-5">
      <div className={`flex h-9 w-9 items-center justify-center border border-white/10 bg-white/[0.03] ${accent}`}>
        <Icon className="h-4 w-4" />
      </div>
      <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500">{label}</p>
      <p className="mt-1 font-display text-2xl font-black">{value}</p>
    </div>
  );
}

function ListCard({ icon: Icon, title, items, testId }) {
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <div data-testid={testId} className="border border-white/10 bg-[#0A0A0B] p-6">
      <div className="mb-4 flex items-center gap-2 text-primary">
        <Icon className="h-4 w-4" />
        <h3 className="font-display font-semibold text-white">{title}</h3>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-zinc-500">No data yet.</p>
      ) : (
        <div className="space-y-2.5">
          {items.map((it, i) => (
            <div key={i}>
              <div className="flex items-center justify-between text-sm">
                <span className="truncate pr-3 text-zinc-300">{it.label}</span>
                <span className="font-mono text-zinc-400">{it.count}</span>
              </div>
              <div className="mt-1 h-1.5 w-full bg-white/5">
                <div className="h-full bg-primary/70" style={{ width: `${(it.count / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = (d) => {
    setLoading(true);
    api.get(`/admin/analytics?days=${d}`).then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
  };

  useEffect(() => { load(days); /* eslint-disable-next-line */ }, [days]);

  if (loading && !data) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }
  if (!data) return <p className="py-8 text-zinc-400">Couldn't load analytics.</p>;

  const t = data.totals;

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold">Website Analytics</h2>
          <p className="text-sm text-zinc-500">Traffic, sources, and where visitors drop off.</p>
        </div>
        <div className="flex gap-1 border border-white/10 bg-[#0A0A0B] p-1">
          {RANGES.map((r) => (
            <button
              key={r.value}
              data-testid={`range-${r.value}`}
              onClick={() => setDays(r.value)}
              className={`rounded-sm px-3 py-1.5 text-xs font-semibold transition-colors ${days === r.value ? "bg-primary text-white" : "text-zinc-400 hover:text-white"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
        <StatCard testId="stat-pageviews" icon={Eye} label="Pageviews" value={t.pageviews} />
        <StatCard testId="stat-visitors" icon={Users} label="Unique visitors" value={t.unique_visitors} />
        <StatCard testId="stat-sessions" icon={MousePointerClick} label="Sessions" value={t.sessions} />
        <StatCard testId="stat-avg" icon={Timer} label="Avg. time" value={fmtDuration(t.avg_session_ms)} />
        <StatCard testId="stat-bounce" icon={LogOut} label="Bounce rate" value={`${t.bounce_rate}%`} accent="text-amber-400" />
        <StatCard testId="stat-signups" icon={UserPlus} label="Signups" value={t.signups} accent="text-emerald-400" />
        <StatCard testId="stat-bookings" icon={CalendarCheck} label="Bookings" value={t.bookings} accent="text-emerald-400" />
      </div>

      {/* Traffic over time */}
      <div className="border border-white/10 bg-[#0A0A0B] p-6">
        <h3 className="mb-4 font-display font-semibold">Visitors over time</h3>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <AreaChart data={data.series} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="vg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 11 }} tickFormatter={(d) => format(parseISO(d), "MMM d")} minTickGap={24} />
              <YAxis tick={{ fill: "#71717a", fontSize: 11 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: "#0A0A0B", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "#fff" }}
                labelFormatter={(d) => format(parseISO(d), "EEE, MMM d")}
              />
              <Area type="monotone" dataKey="views" stroke="#3B82F6" strokeWidth={2} fill="url(#vg)" name="Pageviews" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <ListCard testId="top-pages" icon={Globe} title="Top pages" items={data.top_pages} />
        <ListCard testId="top-sources" icon={MousePointerClick} title="Where visitors came from" items={data.top_sources} />
        <ListCard testId="exit-pages" icon={DoorOpen} title="Where they left (exit pages)" items={data.exit_pages} />
        <ListCard testId="devices" icon={Smartphone} title="Devices" items={data.devices} />
      </div>

      {/* Recent activity */}
      <div className="border border-white/10 bg-[#0A0A0B] p-6" data-testid="recent-activity">
        <h3 className="mb-4 font-display font-semibold">Recent activity</h3>
        {data.recent.length === 0 ? (
          <p className="text-sm text-zinc-500">No visits recorded yet — data starts flowing as people browse the site.</p>
        ) : (
          <div className="divide-y divide-white/5">
            {data.recent.map((e, i) => (
              <div key={i} className="flex items-center justify-between py-2.5 text-sm">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-zinc-300">{e.path}</span>
                  {e.authed && <Badge className="rounded-sm bg-primary/15 text-[10px] text-primary">member</Badge>}
                </div>
                <div className="flex items-center gap-4 text-xs text-zinc-500">
                  <span>{e.source}</span>
                  <span>{e.device}</span>
                  <span>{e.ts ? format(parseISO(e.ts), "MMM d, HH:mm") : ""}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-zinc-600">
        Note: detailed page-by-page traffic is tracked from the moment this feature went live — earlier visits weren't
        recorded. Signups &amp; bookings counts, however, reflect your full history.
      </p>
    </div>
  );
}
